import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { accountApi } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtDate } from "../companies/CompanyPicker";
import { xlsxBook, type XCell } from "../lib/xlsx";
import { lastCompany } from "../companies/useMe";

// ST09: the company's security and its data — require two-step sign-in, who has access and when they last signed
// in, a full export (Excel and JSON), and deleting the company (30 days to restore)
export function SecurityDataPage() {
  const { company } = useCompany();
  const nav = useNavigate();
  const [rev, setRev] = useState(0);
  const sec = useLoad(() => accountApi.security(company.id), [company.id, rev]);
  const review = useLoad(() => accountApi.accessReview(company.id).catch(() => null), [company.id]);
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false); const [confirm, setConfirm] = useState("");
  const save = (blob: Blob, name: string) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
  const exportAll = async (kind: "json" | "xlsx") => {
    setBusy(true); setErr(null);
    try {
      const x = await accountApi.exportCompany(company.id);
      const base = `${company.name.replace(/[^\w-]+/g, "-")}-${x.exported_at.slice(0, 10)}`;
      if (kind === "json") save(new Blob([JSON.stringify(x, null, 1)], { type: "application/json" }), `${base}.json`);
      else {
        const sheets = Object.entries(x.tables).map(([name, rows]) => {
          const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
          return { name: name.replace(/^books(_in|_cloud)?\./, ""), columns: cols.map((c) => ({ label: c })),
            rows: rows.map((r) => cols.map((c) => { const v = r[c]; return (v != null && typeof v === "object" ? JSON.stringify(v) : v) as XCell; })) };
        });
        save(new Blob([xlsxBook(sheets) as Uint8Array<ArrayBuffer>], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${base}.xlsx`);
      }
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const del = () => { setBusy(true); setErr(null);
    accountApi.deleteCompany(company.id, confirm).then(() => { lastCompany.clear(); nav("/companies", { replace: true }); }).catch((e) => setErr(errText(e))).finally(() => setBusy(false)); };
  if (!sec.data) return <section className="page"><Loading /><ErrorNote text={sec.error?.message} /></section>;
  return (
    <section className="page">
      <PageHead crumb="Settings" title="Security and data" code="ST09" />
      <ErrorNote text={err} />
      <section className="card pad"><h2>Two-step sign-in</h2>
        <label className="check"><input type="checkbox" disabled={!sec.data.can.edit} checked={sec.data.require_mfa}
          onChange={(e) => accountApi.saveSecurity(company.id, e.target.checked).then(() => setRev((r) => r + 1)).catch((x) => setErr(errText(x)))} /> Everyone who uses this company signs in with a second step</label>
        <p className="muted small">Each person sets it up under <a href="/account">Your account</a>. Turn it on only after you have it yourself{sec.data.my_aal === "aal2" ? " (you do)" : ""}.</p>
      </section>
      {review.data && <section className="card pad"><h2>Who has access</h2>
        <table className="grid-table"><thead><tr><th>Person</th><th>Role</th><th>Added</th><th>Last signed in</th></tr></thead>
          <tbody>{review.data.people.map((p) => <tr key={p.user_id} className={p.active ? "" : "muted"}><td>{p.name ?? p.email}<br /><span className="muted small">{p.email}</span></td>
            <td>{p.custom_role ?? p.role}{!p.active && " (removed)"}</td><td className="small">{fmtDate(p.added_at.slice(0, 10))}</td>
            <td className="small">{p.last_sign_in ? fmtDate(p.last_sign_in.slice(0, 10)) : "—"}</td></tr>)}
            {review.data.firm_people.map((p) => <tr key={`f${p.user_id}`}><td>{p.name ?? p.email}<br /><span className="muted small">{p.email}</span></td>
              <td>{p.role} · through {p.firm}</td><td></td><td className="small">{p.last_sign_in ? fmtDate(p.last_sign_in.slice(0, 10)) : "—"}</td></tr>)}</tbody></table>
        <p className="muted small">Review this every quarter: remove people who have left (ST02), and firms you no longer work with.</p>
      </section>}
      <section className="card pad"><h2>Your data</h2>
        <p className="small">Everything in this company — every entry, account, party, item, setting and log — as an Excel workbook (a sheet per table) or JSON. Passwords and keys are left out.</p>
        <div className="actions"><button disabled={busy} onClick={() => void exportAll("xlsx")}>Download Excel</button><button disabled={busy} onClick={() => void exportAll("json")}>Download JSON</button></div>
        <p className="muted small">Altekore also keeps nightly backups of the whole database (Supabase, Mumbai), restored to a test copy every month.</p>
      </section>
      <section className="card pad danger-zone"><h2>Delete this company</h2>
        <p className="small">It disappears for everyone at once. An owner can restore it from Your account for 30 days; after that it is removed for good. Download the data first.</p>
        <div className="fields inline"><label>Type the company's name<input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={company.name} /></label>
          <button className="danger" disabled={busy || confirm.trim().toLowerCase() !== company.name.toLowerCase()} onClick={del}>Delete</button></div>
      </section>
    </section>
  );
}

// /status: is Books up (no sign-in needed)
export function StatusPage() {
  const { data, error } = useLoad(() => accountApi.health(), []);
  return (
    <main className="page status-page">
      <h1>Altekore Books status</h1>
      {error ? <p className="warn">Books can't be reached right now. {error.message}</p> : !data ? <Loading /> : <ul className="status-list">
        <li className="ok">The app and database are up · {new Date(data.at).toLocaleString("en-IN")}</li>
        {data.scheduler && data.scheduler.ok !== null && <li className={data.scheduler.ok ? "ok" : "warn"}>Scheduled work (sending, reminders, payment checks): {data.scheduler.ok ? "running" : "delayed"}</li>}
        {data.services && Object.keys(data.services).length > 0 && <li className="ok">Connected services: {Object.keys(data.services).join(", ")}</li>}
        {data.gsp !== undefined && <li className={data.gsp ? "ok" : "muted"}>GST and TDS filing (Sandbox): {data.gsp ? "connected" : "not set up"}</li>}
      </ul>}
    </main>
  );
}
